import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

export type NodeStorageMetricsStep = '1m' | '5m' | '1h';

const NODE_STORAGE_METRICS_QUERY_KEY = 'node-storage-metrics';
const getNodeStorageMetricsQueryKey = (params: {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
  storageType: string;
  startTime: string | undefined;
  endTime: string | undefined;
  step: NodeStorageMetricsStep;
}) =>
  [
    NODE_STORAGE_METRICS_QUERY_KEY,
    params.organizationId,
    params.clusterId,
    params.nodeName,
    params.storageType,
    params.startTime,
    params.endTime,
    params.step,
  ] as const;

type NodeStorageMetricsQueryParams = {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
  storageType: string;
  startTime: string | undefined;
  endTime: string | undefined;
  step: NodeStorageMetricsStep;
  enabled?: boolean;
};

const getNodeStorageMetricsQueryOptions = ({
  organizationId,
  clusterId,
  nodeName,
  storageType,
  startTime,
  endTime,
  step,
  enabled,
}: NodeStorageMetricsQueryParams) =>
  queryOptions({
    queryKey: getNodeStorageMetricsQueryKey({
      organizationId,
      clusterId,
      nodeName,
      storageType,
      startTime,
      endTime,
      step,
    }),
    queryFn: async () => {
      const { data } = await apiClient.stop.getNodeStorageMetrics({
        organizationId: organizationId!,
        clusterId: clusterId!,
        nodeName: nodeName!,
        step,
        storageType,
        startTime,
        endTime,
      });

      return data;
    },
    enabled: makeIsEnabled(!!organizationId, !!clusterId, !!nodeName, enabled),
  });

type UseNodeStorageMetricsQueryArgs = {
  clusterId?: string;
  nodeName?: string;
  storageType: string;
  startTime?: string;
  endTime?: string;
  enabled?: boolean;
  step?: NodeStorageMetricsStep;
};

export const useNodeStorageMetricsQuery = ({
  clusterId,
  nodeName,
  storageType,
  startTime,
  endTime,
  enabled,
  step = '1h',
}: UseNodeStorageMetricsQueryArgs) => {
  const { currentOrganization } = useOrganizations();

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    getNodeStorageMetricsQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      nodeName,
      storageType,
      startTime,
      endTime,
      step,
      enabled,
    })
  );

  return {
    series: data?.series ?? [],
    summary: data?.summary,
    isLoading: isLoading || isFetching,
    error,
    refetch,
  };
};
